/**
 * Aplica escudos desde la caché local del torneo (misma clave que grupos/partidos).
 */
export function applyLeagueMatchShields(match, tournamentId) {
  if (!match || !tournamentId) return match;
  let images = [];
  try {
    const raw = localStorage.getItem(`${tournamentId}-images`);
    if (raw) images = JSON.parse(raw);
  } catch {
    return match;
  }
  if (!Array.isArray(images)) return match;
  const out = { ...match };
  if (out.localTeam) {
    out.localTeam = { ...out.localTeam };
    const li = images.find((i) => i.id === out.localTeam.id);
    if (li?.imageData) out.localTeam.shield = li.imageData;
  }
  if (out.foreignTeam) {
    out.foreignTeam = { ...out.foreignTeam };
    const fi = images.find((i) => i.id === out.foreignTeam.id);
    if (fi?.imageData) out.foreignTeam.shield = fi.imageData;
  }
  return out;
}

export function buildLeagueMatchUpdatePayload(match, { localGoals, foreignGoals, isActive, completed }) {
  const lg = Math.max(0, Math.min(99, Number(localGoals)));
  const fg = Math.max(0, Math.min(99, Number(foreignGoals)));
  let winnerTeamId = null;
  if (completed) {
    if (lg > fg) winnerTeamId = match.localTeamId;
    else if (fg > lg) winnerTeamId = match.foreignTeamId;
    else winnerTeamId = null;
  }
  return {
    tournamentId: match.tournamentId,
    localTeamId: match.localTeamId,
    foreignTeamId: match.foreignTeamId,
    winnerTeamId,
    dayHour: match.dayHour,
    localGoals: lg,
    foreignGoals: fg,
    fieldName: match.fieldName ?? null,
    isActive,
    completed,
  };
}

/** Etiqueta visible para un bando de un partido de eliminatoria (equipo o posición de grupo). */
export function bracketSideLabel(match, side) {
  if (!match) return side === 'local' ? 'Local' : 'Visitante';
  if (side === 'local') {
    if (match.localTeam?.name) return match.localTeam.name;
    if (match.groupPositionLocal?.team?.name) return match.groupPositionLocal.team.name;
    if (match.groupPositionLocal?.name) return match.groupPositionLocal.name;
    return 'Local';
  }
  if (match.foreignTeam?.name) return match.foreignTeam.name;
  if (match.groupPositionForeign?.team?.name) return match.groupPositionForeign.team.name;
  if (match.groupPositionForeign?.name) return match.groupPositionForeign.name;
  return 'Visitante';
}

export function bracketSideShield(match, side) {
  if (!match) return undefined;
  if (side === 'local') {
    if (match.localTeam?.shield) return match.localTeam.shield;
    return match.groupPositionLocal?.team?.shield;
  }
  if (match.foreignTeam?.shield) return match.foreignTeam.shield;
  return match.groupPositionForeign?.team?.shield;
}

/** Escudos desde caché para bracket (equipos directos o a través de posición de grupo). */
export function applyBracketMatchShields(match, tournamentId) {
  if (!match || !tournamentId) return match;
  let images = [];
  try {
    const raw = localStorage.getItem(`${tournamentId}-images`);
    if (raw) images = JSON.parse(raw);
  } catch {
    return match;
  }
  if (!Array.isArray(images)) return match;
  const out = { ...match };
  const patchTeam = (team) => {
    if (!team?.id) return team;
    const t = { ...team };
    const img = images.find((i) => i.id === t.id);
    if (img?.imageData) t.shield = img.imageData;
    return t;
  };
  if (out.localTeam) out.localTeam = patchTeam(out.localTeam);
  if (out.foreignTeam) out.foreignTeam = patchTeam(out.foreignTeam);
  if (out.groupPositionLocal) {
    out.groupPositionLocal = { ...out.groupPositionLocal };
    if (out.groupPositionLocal.team) {
      out.groupPositionLocal.team = patchTeam(out.groupPositionLocal.team);
    }
  }
  if (out.groupPositionForeign) {
    out.groupPositionForeign = { ...out.groupPositionForeign };
    if (out.groupPositionForeign.team) {
      out.groupPositionForeign.team = patchTeam(out.groupPositionForeign.team);
    }
  }
  return out;
}

export function effectiveBracketSideTeamId(match, side) {
  if (!match) return null;
  if (side === 'local') {
    if (match.localTeamId) return match.localTeamId;
    return match.groupPositionLocal?.teamId ?? match.groupPositionLocal?.team?.id ?? null;
  }
  if (match.foreignTeamId) return match.foreignTeamId;
  return match.groupPositionForeign?.teamId ?? match.groupPositionForeign?.team?.id ?? null;
}

/** True solo si local y visitante están ya resueltos a un equipo (no solo plaza vacía). */
export function bracketMatchHasBothTeams(match) {
  const a = effectiveBracketSideTeamId(match, 'local');
  const b = effectiveBracketSideTeamId(match, 'foreign');
  return Boolean(a && b);
}

export function buildBracketMatchUpdatePayload(match, { localGoals, foreignGoals, isActive, completed }) {
  const lg = Math.max(0, Math.min(99, Number(localGoals)));
  const fg = Math.max(0, Math.min(99, Number(foreignGoals)));
  let winnerTeamId = null;
  if (completed) {
    const localId = effectiveBracketSideTeamId(match, 'local');
    const foreignId = effectiveBracketSideTeamId(match, 'foreign');
    if (lg > fg) winnerTeamId = localId;
    else if (fg > lg) winnerTeamId = foreignId;
    else winnerTeamId = null;
  }
  return {
    tournamentId: match.tournamentId,
    name: match.name,
    dayHour: match.dayHour,
    nextMatchId: match.nextMatchId ?? null,
    localTeamId: match.localTeamId ?? null,
    foreignTeamId: match.foreignTeamId ?? null,
    winnerTeamId,
    groupPositionLocalId: match.groupPositionLocalId ?? null,
    groupPositionForeignId: match.groupPositionForeignId ?? null,
    localGoals: lg,
    foreignGoals: fg,
    fieldName: match.fieldName ?? null,
    isActive,
    completed,
    order: match.order ?? null,
  };
}
